import * as Sentry from "@sentry/react";
import axios from "axios";
import type { Folder } from "@theirstory/common/api_types/backend";

import type { ActionTypeHelper } from "../state/rootStore";

import { notifySuccess } from "./notify";
import {
  FOLDERS_LOADED,
  FOLDER_DETAILS_LOADED,
  FOLDER_STORIES_LOADED,
  FOLDER_STORIES_LOADING,
  FOLDER_STORIES_SET_PAGE_VIEW,
  FOLDER_STORIES_SORT_SETTING,
  FOLDERS_RESET_CURRENT,
  FOLDERS_LOADING,
  FOLDERS_TOGGLE_CREATE_FOLDER_MODAL,
  RESET_FOLDER_STATE,
  RESET_FOLDER_STORIES,
} from "./types";

export const getProjectFolders =
  (projectId: string): ActionTypeHelper<Promise<void>> =>
  async (dispatch) => {
    try {
      dispatch({ type: FOLDERS_LOADING });
      const { data } = await axios.get(`/projects/${projectId}/folders`);
      dispatch({ type: FOLDERS_LOADED, payload: data });
    } catch (err) {
      console.log(err);
      Sentry.captureException(err);
    }
  };

export const getFolder =
  (folderId: string): ActionTypeHelper<Promise<void>> =>
  async (dispatch) => {
    dispatch(loadFolderDetails(folderId));
    dispatch(loadFolderStories({ folderId }));
  };

export const loadFolderDetails = (folderId: string) => async (dispatch) => {
  try {
    const { data } = await axios.get(`/folders/${folderId}`);
    dispatch({ type: FOLDER_DETAILS_LOADED, payload: { folderDetails: data } });
  } catch (error) {
    console.log(error);
    throw error;
  }
};

export const loadFolderStories = (params: { folderId: string }) => async (dispatch, getState) => {
  try {
    const {
      folders: {
        folder: {
          paging: { size: pageSize, current: currentPage },
          sort: { column, order },
        },
      },
    } = getState();

    const { folderId } = params;
    // We only put the loading circular progress the first time we load the stories
    if (currentPage === 1) {
      dispatch({ type: FOLDER_STORIES_LOADING });
    }
    const urlPath = `/folders/${folderId}/stories`;
    const searchParam = new URLSearchParams(window.location.search).get("search");

    const res = await axios.get(urlPath, {
      params: { pageSize, page: currentPage, search: searchParam, sort: column, direction: order },
    });

    const { items: newStories, total } = res.data;
    const stories = [...getState().folders.folder.stories, ...newStories] ?? [];

    let page = currentPage;
    if (stories.length < total) {
      page = currentPage + 1;
    }

    dispatch({ type: FOLDER_STORIES_LOADED, payload: { stories, total, page, folderId } });
    return stories;
  } catch (err) {
    console.log(err);
    throw err;
  }
};

export const setFolderStoriesPageView =
  ({ currentPage, pageSize }: any) =>
  async (dispatch) => {
    dispatch({ type: FOLDER_STORIES_SET_PAGE_VIEW, payload: { currentPage, pageSize } });
  };

export const setFolderStoriesSort = (column, order) => async (dispatch) => {
  dispatch({ type: FOLDER_STORIES_SORT_SETTING, payload: { column, order } });
};

export const editFolder = (projectId: string, folderId: string, values) => async (dispatch) => {
  try {
    const res = await axios.put(`/folders/${folderId}/edit`, values);
    dispatch(getProjectFolders(projectId));
    return res.data;
  } catch (error) {
    console.log(error);
    throw error;
  }
};

export const deleteFolder = (projectId: string, folderId: string) => async (dispatch) => {
  try {
    dispatch({ type: FOLDERS_LOADING });
    const res = await axios.delete(`/folders/${folderId}`);
    dispatch(notifySuccess(`Successfully deleted the folder`));
    return res.data;
  } catch (error) {
    console.log(error);
    throw error;
  }
};

export const createFolder = (projectId: string, name: string) => async (dispatch) => {
  try {
    dispatch({ type: FOLDERS_LOADING });
    const res = await axios.post("/folders/create", {
      name,
      project: projectId,
    });
    dispatch(notifySuccess(`Successfully created the folder, ${res.data.name}`));
    dispatch(getProjectFolders(projectId));
  } catch (error) {
    console.log(error);
    throw error;
  }
};

export const resetCurrentFolder = () => async (dispatch) => {
  dispatch({ type: FOLDERS_RESET_CURRENT });
};

export const resetFolderState = () => async (dispatch) => {
  dispatch({ type: RESET_FOLDER_STATE });
};

export const resetFolderStories = () => async (dispatch) => {
  dispatch({ type: RESET_FOLDER_STORIES });
};

export const openCreateFolderModal = (isOpen) => async (dispatch) => {
  dispatch({ type: FOLDERS_TOGGLE_CREATE_FOLDER_MODAL, payload: { isOpen: isOpen } });
};

export type FoldersLoaded = Folder[];
